/**
 * OutpaintTab Component
 *
 * Allows users to expand images beyond their original boundaries.
 * Uses the same inpaint endpoint with outpaint_directions and outpaint_distance_* parameters.
 */

import { useReducer, useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { ImageUploader } from '../ImageUploader';
import { ImageModelSelector } from '../ImageModelSelector';
import { StyleSelector } from '../StyleSelector';
import { ImageResultDisplay } from '../ImageResultDisplay';
import { imageGenerationAPI } from '@/services/imageGenerationAPI';
import type { OutpaintDirection } from '@/types';
import { outpaintReducer, initialState } from './outpaintReducer';

export function OutpaintTab() {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(outpaintReducer, initialState);

  const {
    imageBase64,
    imageFile,
    prompt,
    negativePrompt,
    showNegativePrompt,
    selectedStyles,
    model,
    distances,
    isProcessing,
    error,
    result,
  } = state;

  // Calculate which directions are active (distance > 0)
  const activeDirections = useMemo(() => {
    const dirs: OutpaintDirection[] = [];
    if (distances.Top > 0) dirs.push('Top');
    if (distances.Bottom > 0) dirs.push('Bottom');
    if (distances.Left > 0) dirs.push('Left');
    if (distances.Right > 0) dirs.push('Right');
    return dirs;
  }, [distances]);

  const handleImageSelect = (base64: string, file: File) => {
    dispatch({ type: 'SET_IMAGE', payload: { base64, file } });
  };

  const handleRemoveImage = () => {
    dispatch({ type: 'REMOVE_IMAGE' });
  };

  const handleDistanceChange = (direction: OutpaintDirection, value: number) => {
    dispatch({ type: 'SET_DISTANCE', payload: { direction, value } });
  };

  const handleGenerate = async () => {
    if (!imageBase64) {
      dispatch({
        type: 'SET_ERROR',
        payload: t('image.upscaleVary.errors.noImage', {
          defaultValue: 'Please upload an image first',
        }),
      });
      return;
    }

    if (activeDirections.length === 0) {
      dispatch({
        type: 'SET_ERROR',
        payload: t('image.outpaint.noDirectionsWarning', {
          defaultValue: 'Please set at least one direction with expansion > 0',
        }),
      });
      return;
    }

    dispatch({ type: 'SET_PROCESSING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_RESULT', payload: null });

    try {
      const response = await imageGenerationAPI.inpaintImage({
        image: imageBase64,
        prompt: prompt.trim() || undefined,
        negative_prompt: negativePrompt.trim() || undefined,
        styles: selectedStyles.length > 0 ? selectedStyles : undefined,
        outpaint_directions: activeDirections,
        outpaint_distance_left: distances.Left,
        outpaint_distance_right: distances.Right,
        outpaint_distance_top: distances.Top,
        outpaint_distance_bottom: distances.Bottom,
        async_process: false,
        model,
      });

      if (response.success && response.data) {
        // Defensive: ensure images is always an array
        const images = Array.isArray(response.data.images) ? response.data.images : [];
        if (images.length > 0) {
          dispatch({
            type: 'SET_RESULT',
            payload: {
              images,
              processingTime: response.data.processing_time_seconds || 0,
            },
          });
        } else {
          dispatch({
            type: 'SET_ERROR',
            payload: t('image.upscaleVary.errors.noResult', {
              defaultValue: 'No images were generated',
            }),
          });
        }
      } else {
        dispatch({
          type: 'SET_ERROR',
          payload:
            response.error ||
            t('image.upscaleVary.errors.processingFailed', {
              defaultValue: 'Failed to process image',
            }),
        });
      }
    } catch {
      dispatch({
        type: 'SET_ERROR',
        payload: t('image.upscaleVary.errors.networkError', {
          defaultValue: 'Network error. Please try again.',
        }),
      });
    } finally {
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  };

  const handleReset = () => {
    dispatch({ type: 'RESET_RESULT' });
  };

  const canGenerate = imageBase64 && activeDirections.length > 0 && !isProcessing;

  // If we have results, show them
  if (result) {
    return (
      <ImageResultDisplay
        images={result.images}
        processingTime={result.processingTime}
        onReset={handleReset}
      />
    );
  }

  // Calculate preview dimensions (scaled for visualization)
  const baseSize = 180; // Base image size in pixels
  const maxExpansion = 128; // Max visual expansion for preview
  const scale = maxExpansion / 512; // Scale factor for preview

  const previewTop = Math.round(distances.Top * scale);
  const previewBottom = Math.round(distances.Bottom * scale);
  const previewLeft = Math.round(distances.Left * scale);
  const previewRight = Math.round(distances.Right * scale);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
          {t('image.outpaint.title', {
            defaultValue: 'Outpaint',
          })}
        </h2>
        <p className="text-secondary-600 dark:text-secondary-400 mt-1">
          {t('image.outpaint.description', {
            defaultValue: 'Expand your image beyond its original boundaries',
          })}
        </p>
      </div>

      {/* Image Upload */}
      <div>
        <label className="form-label">
          {t('image.outpaint.uploadPrompt', {
            defaultValue: 'Upload an image to expand',
          })}
        </label>
        <ImageUploader
          onImageSelect={handleImageSelect}
          onRemove={handleRemoveImage}
          disabled={isProcessing}
          maxSizeMB={10}
          previewUrl={imageFile ? URL.createObjectURL(imageFile) : undefined}
        />
      </div>

      {/* Direction Selector with Visual Preview */}
      {imageBase64 && (
        <div className="space-y-4">
          <div>
            <label className="form-label">
              {t('image.outpaint.directions', {
                defaultValue: 'Expansion Directions',
              })}
            </label>
            <p className="text-xs text-secondary-500 dark:text-secondary-400">
              {t('image.outpaint.selectDirections', {
                defaultValue:
                  'Adjust sliders to set expansion distance for each side (0 = no expansion)',
              })}
            </p>
          </div>

          {/* Visual Preview Container */}
          <div className="bg-secondary-100 dark:bg-secondary-800 rounded-xl p-6 overflow-hidden">
            {/* Preview with expansion areas */}
            <div className="flex justify-center">
              <div
                className="relative"
                style={{
                  width: baseSize + previewLeft + previewRight,
                  height: baseSize + previewTop + previewBottom,
                }}
              >
                {/* Top expansion area */}
                {distances.Top > 0 && (
                  <div
                    className="absolute left-0 right-0 top-0 bg-blue-500/30 border-2 border-dashed border-blue-500 flex items-center justify-center"
                    style={{
                      height: previewTop,
                      marginLeft: previewLeft,
                      marginRight: previewRight,
                      width: baseSize,
                    }}
                  >
                    <span className="text-blue-700 dark:text-blue-300 text-xs font-medium bg-white/80 dark:bg-secondary-900/80 px-2 py-0.5 rounded">
                      +{distances.Top}px
                    </span>
                  </div>
                )}

                {/* Bottom expansion area */}
                {distances.Bottom > 0 && (
                  <div
                    className="absolute left-0 right-0 bottom-0 bg-blue-500/30 border-2 border-dashed border-blue-500 flex items-center justify-center"
                    style={{
                      height: previewBottom,
                      marginLeft: previewLeft,
                      marginRight: previewRight,
                      width: baseSize,
                    }}
                  >
                    <span className="text-blue-700 dark:text-blue-300 text-xs font-medium bg-white/80 dark:bg-secondary-900/80 px-2 py-0.5 rounded">
                      +{distances.Bottom}px
                    </span>
                  </div>
                )}

                {/* Left expansion area */}
                {distances.Left > 0 && (
                  <div
                    className="absolute left-0 bg-green-500/30 border-2 border-dashed border-green-500 flex items-center justify-center"
                    style={{
                      width: previewLeft,
                      top: previewTop,
                      height: baseSize,
                    }}
                  >
                    <span className="text-green-700 dark:text-green-300 text-xs font-medium bg-white/80 dark:bg-secondary-900/80 px-1 py-0.5 rounded rotate-[-90deg]">
                      +{distances.Left}px
                    </span>
                  </div>
                )}

                {/* Right expansion area */}
                {distances.Right > 0 && (
                  <div
                    className="absolute right-0 bg-green-500/30 border-2 border-dashed border-green-500 flex items-center justify-center"
                    style={{
                      width: previewRight,
                      top: previewTop,
                      height: baseSize,
                    }}
                  >
                    <span className="text-green-700 dark:text-green-300 text-xs font-medium bg-white/80 dark:bg-secondary-900/80 px-1 py-0.5 rounded rotate-90">
                      +{distances.Right}px
                    </span>
                  </div>
                )}

                {/* Corner fills */}
                {distances.Top > 0 && distances.Left > 0 && (
                  <div
                    className="absolute top-0 left-0 bg-purple-500/20 border-2 border-dashed border-purple-400"
                    style={{ width: previewLeft, height: previewTop }}
                  />
                )}
                {distances.Top > 0 && distances.Right > 0 && (
                  <div
                    className="absolute top-0 right-0 bg-purple-500/20 border-2 border-dashed border-purple-400"
                    style={{ width: previewRight, height: previewTop }}
                  />
                )}
                {distances.Bottom > 0 && distances.Left > 0 && (
                  <div
                    className="absolute bottom-0 left-0 bg-purple-500/20 border-2 border-dashed border-purple-400"
                    style={{ width: previewLeft, height: previewBottom }}
                  />
                )}
                {distances.Bottom > 0 && distances.Right > 0 && (
                  <div
                    className="absolute bottom-0 right-0 bg-purple-500/20 border-2 border-dashed border-purple-400"
                    style={{ width: previewRight, height: previewBottom }}
                  />
                )}

                {/* Original image */}
                <div
                  className="absolute rounded-lg overflow-hidden shadow-lg ring-2 ring-secondary-300 dark:ring-secondary-600"
                  style={{
                    left: previewLeft,
                    top: previewTop,
                    width: baseSize,
                    height: baseSize,
                  }}
                >
                  <img
                    src={imageFile ? URL.createObjectURL(imageFile) : ''}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <span className="text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                      {t('image.outpaint.original', { defaultValue: 'Original' })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Total size info */}
            <div className="mt-4 text-center text-sm text-secondary-600 dark:text-secondary-400">
              {t('image.outpaint.finalSize', {
                defaultValue: 'Final size: Original + {{width}}px width, + {{height}}px height',
                width: distances.Left + distances.Right,
                height: distances.Top + distances.Bottom,
              })}
            </div>
          </div>

          {/* Sliders Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Top */}
            <div className="card-themed">
              <div className="flex items-center justify-between mb-2">
                <label className="label-inline">
                  {t('image.outpaint.top', { defaultValue: 'Top' })}
                </label>
                <span className="text-sm font-mono text-blue-600 dark:text-blue-400">
                  {distances.Top}px
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="512"
                step="8"
                value={distances.Top}
                onChange={(e) => handleDistanceChange('Top', parseInt(e.target.value))}
                disabled={isProcessing}
                className="slider-blue"
              />
              <div className="flex justify-between text-xs text-white/40 mt-1">
                <span>0</span>
                <span>512</span>
              </div>
            </div>

            {/* Bottom */}
            <div className="card-themed">
              <div className="flex items-center justify-between mb-2">
                <label className="label-inline">
                  {t('image.outpaint.bottom', { defaultValue: 'Bottom' })}
                </label>
                <span className="text-sm font-mono text-blue-600 dark:text-blue-400">
                  {distances.Bottom}px
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="512"
                step="8"
                value={distances.Bottom}
                onChange={(e) => handleDistanceChange('Bottom', parseInt(e.target.value))}
                disabled={isProcessing}
                className="slider-blue"
              />
              <div className="flex justify-between text-xs text-white/40 mt-1">
                <span>0</span>
                <span>512</span>
              </div>
            </div>

            {/* Left */}
            <div className="card-themed">
              <div className="flex items-center justify-between mb-2">
                <label className="label-inline">
                  {t('image.outpaint.left', { defaultValue: 'Left' })}
                </label>
                <span className="text-sm font-mono text-green-600 dark:text-green-400">
                  {distances.Left}px
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="512"
                step="8"
                value={distances.Left}
                onChange={(e) => handleDistanceChange('Left', parseInt(e.target.value))}
                disabled={isProcessing}
                className="slider-green"
              />
              <div className="flex justify-between text-xs text-white/40 mt-1">
                <span>0</span>
                <span>512</span>
              </div>
            </div>

            {/* Right */}
            <div className="card-themed">
              <div className="flex items-center justify-between mb-2">
                <label className="label-inline">
                  {t('image.outpaint.right', { defaultValue: 'Right' })}
                </label>
                <span className="text-sm font-mono text-green-600 dark:text-green-400">
                  {distances.Right}px
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="512"
                step="8"
                value={distances.Right}
                onChange={(e) => handleDistanceChange('Right', parseInt(e.target.value))}
                disabled={isProcessing}
                className="slider-green"
              />
              <div className="flex justify-between text-xs text-white/40 mt-1">
                <span>0</span>
                <span>512</span>
              </div>
            </div>
          </div>

          {/* Quick presets */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                dispatch({
                  type: 'SET_ALL_DISTANCES',
                  payload: { Top: 0, Bottom: 0, Left: 0, Right: 0 },
                })
              }
              className="px-3 py-1.5 text-xs rounded-lg bg-secondary-200 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-300 dark:hover:bg-secondary-600 transition-colors"
            >
              {t('image.outpaint.presets.reset', { defaultValue: 'Reset All' })}
            </button>
            <button
              type="button"
              onClick={() =>
                dispatch({
                  type: 'SET_ALL_DISTANCES',
                  payload: { Top: 128, Bottom: 128, Left: 128, Right: 128 },
                })
              }
              className="px-3 py-1.5 text-xs rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
            >
              {t('image.outpaint.presets.uniform', { defaultValue: 'Uniform (128px)' })}
            </button>
            <button
              type="button"
              onClick={() =>
                dispatch({
                  type: 'SET_ALL_DISTANCES',
                  payload: { Top: 0, Bottom: 0, Left: 256, Right: 256 },
                })
              }
              className="px-3 py-1.5 text-xs rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
            >
              {t('image.outpaint.presets.widescreen', { defaultValue: 'Widescreen' })}
            </button>
            <button
              type="button"
              onClick={() =>
                dispatch({
                  type: 'SET_ALL_DISTANCES',
                  payload: { Top: 256, Bottom: 256, Left: 0, Right: 0 },
                })
              }
              className="px-3 py-1.5 text-xs rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
            >
              {t('image.outpaint.presets.portrait', { defaultValue: 'Portrait' })}
            </button>
          </div>
        </div>
      )}

      {/* Optional Prompt */}
      {imageBase64 && (
        <div>
          <label htmlFor="prompt" className="form-label">
            {t('image.outpaint.prompt', {
              defaultValue: 'Prompt (optional)',
            })}
          </label>
          <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
            {t('image.outpaint.promptHint', {
              defaultValue: 'Describe what should appear in the expanded areas...',
            })}
          </p>
          <textarea
            id="prompt"
            value={prompt}
            onChange={(e) => dispatch({ type: 'SET_PROMPT', payload: e.target.value })}
            disabled={isProcessing}
            placeholder={t('image.outpaint.promptPlaceholder', {
              defaultValue: 'e.g., "continue the landscape with mountains and sky"',
            })}
            rows={3}
            className="input resize-none"
          />
        </div>
      )}

      {/* Negative Prompt (Optional) */}
      {imageBase64 && (
        <div>
          {showNegativePrompt ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="negative-prompt" className="form-label mb-0">
                  {t('image.prompt.negative.label', {
                    defaultValue: 'Negative Prompt',
                  })}{' '}
                  <span className="text-white/40 font-normal">
                    ({t('common.optional', { defaultValue: 'optional' })})
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'HIDE_NEGATIVE_PROMPT' })}
                  className="text-sm text-secondary-600 hover:text-secondary-800 dark:text-secondary-400 dark:hover:text-secondary-200"
                >
                  {t('image.prompt.negative.hide', { defaultValue: 'Hide' })}
                </button>
              </div>
              <textarea
                id="negative-prompt"
                value={negativePrompt}
                onChange={(e) => dispatch({ type: 'SET_NEGATIVE_PROMPT', payload: e.target.value })}
                disabled={isProcessing}
                placeholder={t('image.prompt.negative.placeholder', {
                  defaultValue: 'What to avoid in the expanded areas...',
                })}
                rows={2}
                className="input resize-none"
              />
            </>
          ) : (
            <button
              type="button"
              onClick={() => dispatch({ type: 'SHOW_NEGATIVE_PROMPT' })}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              + {t('image.prompt.negative.toggle', { defaultValue: 'Add negative prompt' })}
            </button>
          )}
        </div>
      )}

      {/* Optional Styles */}
      {imageBase64 && (
        <StyleSelector
          selectedStyles={selectedStyles}
          onChange={(styles) => dispatch({ type: 'SET_SELECTED_STYLES', payload: styles })}
          disabled={isProcessing}
          maxStyles={5}
        />
      )}

      {/* Checkpoint Model Selector */}
      {imageBase64 && (
        <ImageModelSelector
          value={model}
          onChange={(value) => dispatch({ type: 'SET_MODEL', payload: value })}
          provider="fooocus"
          disabled={isProcessing}
        />
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Generate Button */}
      {imageBase64 && (
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className={`
            w-full py-3 px-6 rounded-lg font-medium transition-all
            flex items-center justify-center gap-2
            ${
              canGenerate
                ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                : 'bg-secondary-200 dark:bg-secondary-700 text-secondary-400 dark:text-secondary-500 cursor-not-allowed'
            }
          `}
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              {t('image.outpaint.generating', {
                defaultValue: 'Expanding...',
              })}
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              {t('image.outpaint.generate', {
                defaultValue: 'Expand Image',
              })}
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default OutpaintTab;
